import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import {
  ArrowLeft,
  Button,
  ClockStopwatch,
  Divider,
  Edit02,
  Phone02,
  ShieldDollar,
} from '@v2/ui';
import { isMobileTicket, normalizeTicketNotes } from '@v2/utils';
import pluralize from 'pluralize';
import type { ReactNode } from 'react';
import { prefetchTicketTaxQuote } from '../utils/ticket-tax-quotes';

export const TICKET_SELECTION_FORM_ID = 'ticket-selection-form';

type TicketDetails = Pick<
  TicketGroup,
  | 'id'
  | 'section'
  | 'row'
  | 'format'
  | 'type'
  | 'eticket'
  | 'public_notes'
  | 'instant_delivery'
>;

type Props = {
  ticket: TicketDetails;
  ticketsQuantity: number;
  priceLabel: string;
  isSubmitting?: boolean;
  onBack: () => void;
  seatMap?: ReactNode;
};

export function ConfirmSelection({
  ticket,
  ticketsQuantity,
  priceLabel,
  isSubmitting = false,
  onBack,
  seatMap,
}: Props) {
  const notes = normalizeTicketNotes(ticket.public_notes);
  const ticketType = isMobileTicket(ticket)
    ? 'Mobile'
    : ticket.format !== 'event'
      ? ticket.format.replaceAll('_', ' ')
      : null;
  const prefetchTaxQuote = () => {
    prefetchTicketTaxQuote(ticket.id, ticketsQuantity);
  };

  return (
    <div
      className="w-full bg-white px-5 pb-8 pt-5 lg:h-full lg:px-8 lg:py-8"
      onMouseEnter={prefetchTaxQuote}
    >
      <button
        type="button"
        onClick={onBack}
        className="mb-8 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-primary-500 transition-colors duration-150 hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 lg:mb-7"
      >
        <ArrowLeft
          size="20"
          aria-hidden="true"
          pathProps={{ stroke: 'currentColor' }}
        />
        Back to all tickets
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
            Section {ticket.section}, Row {ticket.row}
          </h2>
          <p className="mt-2 text-base text-gray-700 lg:text-lg">
            You will get {ticketsQuantity}{' '}
            {pluralize('ticket', ticketsQuantity)} together
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
            {priceLabel}
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-500">Per person</p>
        </div>
      </div>

      {seatMap}

      <Button
        type="submit"
        form={TICKET_SELECTION_FORM_ID}
        isLoading={isSubmitting}
        disabled={isSubmitting}
        className="mt-7 w-full"
        size="xl"
        onMouseEnter={prefetchTaxQuote}
        onFocus={prefetchTaxQuote}
      >
        Continue
      </Button>

      <Divider className="my-8" />

      <div className="space-y-6">
        {ticketType && (
          <div className="flex gap-4">
            <Phone02
              size="24"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-gray-700"
            />
            <p className="font-semibold text-gray-900">
              Ticket type: {ticketType}
            </p>
          </div>
        )}

        {ticket.instant_delivery && (
          <div className="flex gap-4">
            <ClockStopwatch
              size="24"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-gray-700"
              pathProps={{ stroke: 'currentColor' }}
            />
            <p className="font-semibold text-gray-900">Instant delivery</p>
          </div>
        )}

        {notes && (
          <div className="flex gap-4">
            <Edit02
              size="24"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-gray-700"
              pathProps={{ stroke: 'currentColor' }}
            />
            <div className="min-w-0">
              <p className="font-semibold text-gray-900">Seller notes</p>
              <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-700">
                {notes}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <ShieldDollar
            size="24"
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-gray-700"
            pathProps={{ stroke: 'currentColor' }}
          />
          <div>
            <p className="font-semibold text-gray-900">
              Elite Sports Tours Ticket Guarantee
            </p>
            <p className="mt-1 text-sm text-gray-700">
              Your purchase is protected by our 100% Buyer Guarantee.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
